import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { i18next } from "./i18n";
import { DisplacedModal } from "./components/DisplacedModal";
import { SurfaceDisplacedModal } from "./components/SurfaceDisplacedModal";
import { UpgradeRequiredModal } from "./components/UpgradeRequiredModal";
import { useAuthStore } from "./auth/auth_store";
import { applyDocumentPreferences } from "./lib/preferences";
import { reloadForUpgrade } from "./lib/sw_update";
import { useServiceWorkerUpdate } from "./lib/hooks/use_sw_update";
import { useUIStore } from "./store/ui";
import { ThemeProvider } from "./theme/ThemeProvider";
import { DEFAULT_SKIN } from "./theme/skins/default";
import { AuthGateContent } from "./routes/AuthGateContent";
import "./theme/skins/default/styles.css";

/**
 * Root: gates on auth + table membership, then wires the full
 * default-skin layout.
 *
 * The default skin lights up the action bar, settled-summary modal,
 * staleness badge, error toast, and turn highlight. The auth slice
 * (`src/auth/auth_store.ts`) drives a Login form when no signing
 * key is in memory; once login succeeds the Lobby picks (or makes)
 * a table; once `tableIdHex` is set the Game view takes over.
 *
 * Connection wiring: a single WS subscription per `tableIdHex`
 * dispatches incoming frames into the Zustand game store. The
 * action bar reads the latest store state via fine-grained
 * selectors and submits envelopes through the signing path in
 * `session/submit.ts`.
 */
export function App(): JSX.Element {
  const { t } = useTranslation();
  // Hot-apply the user's reduce-motion + button-size preferences to
  // the document root on every change. CSS keys off
  // `html[data-reduce-motion]` (animation gating) and
  // `html[data-action-size]` (action-button sizing). The light / dark
  // theme picker has been retired — the dark palette is the single
  // canonical look, matching the PokerStars / GGPoker / partypoker /
  // 888 evening-felt idiom.
  const reduceMotion = useUIStore((s) => s.reduceMotion);
  const buttonSize = useUIStore((s) => s.buttonSize);
  useEffect(() => {
    applyDocumentPreferences(reduceMotion, buttonSize);
  }, [reduceMotion, buttonSize]);
  return (
    <ThemeProvider initial={DEFAULT_SKIN}>
      {/* Skip-to-content link — first tab stop on every page;
       *  visually hidden until focused. Targets the
       *  `id="main-content"` carried by Login / Lobby / Game
       *  `<main>` landmarks. WCAG 2.4.1 Bypass Blocks. */}
      <a href="#main-content" className="skip-link">
        {t("app.skip_to_content")}
      </a>
      <AuthGate />
    </ThemeProvider>
  );
}

/**
 * App-wide deploy pickup. Mounted at the root because a new build
 * reaching the reader is not a game concern — while this lived inside
 * the game screen's install-prompt hook, a reader sitting in the lobby
 * had nothing polling for a new build and nothing to activate one that
 * was already waiting.
 *
 * The one thing it must not do is reload someone who is AT A TABLE.
 * The session survives a reload but the table does not — they land
 * back in the lobby with a seat to re-claim by hand, and three missed
 * turns bench a player. So the update waits for the lobby, which is
 * where a reload costs nothing. The game screen publishes `atTable` to
 * the eager UI store for exactly this, so the gate can ask without
 * pulling the lazy game chunk into the entry bundle.
 */
function ServiceWorkerUpdateGate(): null {
  const atTable = useUIStore((s) => s.atTable);
  useServiceWorkerUpdate(!atTable);
  return null;
}

function AuthGate(): JSX.Element {
  return (
    <>
      <AuthGateContent />
      <ServiceWorkerUpdateGate />
      <LoginSuccessAnnouncer />
      <UpgradeRequiredGate />
      <DisplacedModalGate />
    </>
  );
}

/**
 * Cross-cutting upgrade-required mount (user-approved policy 1 —
 * min-client-version gate). Reads the EAGER `useUIStore.upgradeRequired`
 * latch (set by the HTTP `426` handler in `session/authed_request.ts`
 * and the WS upgrade-required close / protocol-major mismatch in
 * `ws/client.ts`) so the cold-login entry chunk surfaces the blocking
 * "update required — please refresh" modal without importing the lazy
 * game store.
 *
 * Rendered BEFORE the DisplacedModalGate so it wins when both flags are
 * set: a stale client that is also displaced should be told to update
 * (the fundamental blocker) rather than to re-sign-in onto a build the
 * server will keep rejecting. The reload path runs through
 * `reloadForUpgrade`, which applies a waiting PWA service worker when
 * one is precached (landing the fresh shell) and otherwise hard-reloads.
 */
function UpgradeRequiredGate(): JSX.Element | null {
  const upgradeRequired = useUIStore((s) => s.upgradeRequired);
  if (!upgradeRequired) return null;
  return <UpgradeRequiredModal onReload={reloadForUpgrade} />;
}

/**
 * Cross-cutting notice-modal mount — surfaces the TWO independent
 * notice latches the eager UI slice carries (each modeled like
 * `upgradeRequired`: a one-way latch whose lifetime is decoupled from
 * the teardown that produced it, so the modal dwells instead of
 * flashing for one render):
 *
 *   - `pendingRevokeNotice` (B) — the account session was revoked
 *     (new login / explicit logout). Shows the `DisplacedModal`
 *     ("logged in elsewhere — sign in again"); its CTA / countdown is
 *     the FULL logout completion path. The auth-lane teardown already
 *     fired (`runLogoutSequence`); this latch only governs the modal
 *     dwell. Cleared on dismiss.
 *   - `pendingSurfaceNotice` (A) — a newer surface for the SAME
 *     account took over this table slot. Shows the
 *     `SurfaceDisplacedModal` ("active in another tab — keep going
 *     there, or take over here"). Its take-over CTA routes through
 *     `takeOverSurface()` (reopen this surface's socket); its dismiss
 *     just clears the latch (the user chose the other surface). NOT a
 *     logout.
 *
 * Revoke wins when both are latched (it is the more severe, terminal
 * fact). Both latches live on the EAGER UI slice so this gate reads
 * them WITHOUT importing the game store (keeping the reducer graph in
 * the lazy Game chunk).
 */
function DisplacedModalGate(): JSX.Element | null {
  const pendingRevokeNotice = useUIStore((s) => s.pendingRevokeNotice);
  const pendingSurfaceNotice = useUIStore((s) => s.pendingSurfaceNotice);
  const clearPendingNotices = useUIStore((s) => s.clearPendingNotices);
  const takeOverSurface = useAuthStore((s) => s.takeOverSurface);
  const mySeat = useAuthStore((s) => s.mySeat);
  if (pendingRevokeNotice !== null) {
    return (
      <DisplacedModal
        seated={mySeat !== null}
        onDismiss={clearPendingNotices}
      />
    );
  }
  if (pendingSurfaceNotice !== null) {
    return (
      <SurfaceDisplacedModal
        seated={mySeat !== null}
        onTakeOver={takeOverSurface}
        onDismiss={clearPendingNotices}
      />
    );
  }
  return null;
}

/**
 * Sr-only login-success announcer. Fires an assertive
 * "${username} 님 환영합니다" on the auth slice's `null → set`
 * signing-key transition so a screen-reader user catches the
 * login completion without polling the page chrome. Lives at the
 * App level (sibling of `AuthGateContent`) so it survives the
 * Login → Lobby mount swap. Clears the announcement string after
 * a tick so a future logout → re-login cycle re-announces fresh.
 */
function LoginSuccessAnnouncer(): JSX.Element {
  const signingKey = useAuthStore((s) => s.signingKey);
  const username = useAuthStore((s) => s.username);
  const [welcome, setWelcome] = useState<string>("");
  const prevSigningKeyRef = useRef<Uint8Array | null>(signingKey);
  useEffect(() => {
    const prev = prevSigningKeyRef.current;
    if (prev === null && signingKey !== null) {
      const display = username ?? "";
      setWelcome(
        display !== ""
          ? i18next.t("app.welcome_with_name", { name: display })
          : i18next.t("app.welcome_anon"),
      );
      // Clear the text on the next tick so a future re-login
      // re-announces (aria-live only re-reads on text change).
      const t = window.setTimeout(() => setWelcome(""), 1500);
      prevSigningKeyRef.current = signingKey;
      return () => window.clearTimeout(t);
    }
    prevSigningKeyRef.current = signingKey;
  }, [signingKey, username]);
  return (
    <span
      className="sr-only"
      aria-live="assertive"
      aria-atomic="true"
      data-testid="login-success-announcer"
    >
      {welcome}
    </span>
  );
}
